import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import {
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  clearStoredPostAuthRedirect,
  consumePostAuthRedirect,
  getAuthDestination,
  hasExplicitPostAuthIntent,
} from '../../utils/authRedirect';
import { getPostAuthDefaultRoute, hasCompletedOnboarding } from '../../utils/onboarding';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [usernameStatus, setUsernameStatus] = useState('idle');

  const { register, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!hasExplicitPostAuthIntent(location)) {
      clearStoredPostAuthRedirect();
    }
  }, [location]);

  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus('idle');
      return;
    }

    if (!/^[a-zA-Z0-9][a-zA-Z0-9_]*$/.test(username)) {
      setUsernameStatus('invalid');
      return;
    }

    setUsernameStatus('checking');
    try {
      const response = await authAPI.checkUsernameAvailability(username);
      if (response.data.available) {
        setUsernameStatus('available');
      } else {
        setUsernameStatus('taken');
      }
    } catch (error) {
      console.error('Username availability check failed:', error);
      setUsernameStatus('idle');
    }
  }, []);

  useEffect(() => {
    if (formData.username) {
      const timeoutId = setTimeout(() => {
        checkUsernameAvailability(formData.username);
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    setUsernameStatus('idle');
  }, [formData.username, checkUsernameAvailability]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    if (name === 'username') {
      setUsernameStatus('idle');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 30) {
      newErrors.username = 'Username must be no more than 30 characters';
    } else if (!/^[a-zA-Z0-9][a-zA-Z0-9_]*$/.test(formData.username)) {
      newErrors.username =
        'Username must start with a letter or number and can only contain letters, numbers, and underscores';
    } else if (usernameStatus === 'taken') {
      newErrors.username = 'Username is already taken';
    } else if (usernameStatus === 'invalid') {
      newErrors.username = 'Invalid username format';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        'Password must contain at least one lowercase letter, one uppercase letter, and one number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const { confirmPassword, ...userData } = formData;
    const result = await register(userData);

    if (result.success) {
      // toast.success('Account created successfully! Welcome to OKIO!');
      const fallback = getPostAuthDefaultRoute(result.user);
      navigate(
        hasExplicitPostAuthIntent(location)
          ? consumePostAuthRedirect(
              getAuthDestination(location, fallback),
              (target) =>
                !(hasCompletedOnboarding(result.user) && target.startsWith('/onboarding'))
            )
          : fallback,
        { replace: true }
      );
    } else {
      const errorMessage = result.message;

      if (
        errorMessage.toLowerCase().includes('username') &&
        errorMessage.toLowerCase().includes('taken')
      ) {
        setErrors((prev) => ({ ...prev, username: errorMessage }));
      } else if (
        errorMessage.toLowerCase().includes('email') &&
        errorMessage.toLowerCase().includes('exists')
      ) {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
      } else {
        toast.error(errorMessage);
      }
    }
  };

  return (
    <div className="mobile-auth-container relative overflow-hidden">
      <div
        className="lg:hidden absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/home/forrest2.jpeg')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-gray-900/40 to-black/20"></div>
      </div>

      <div
        className="lg:hidden absolute left-4 z-20"
        style={{ top: 'calc(var(--app-safe-area-top) + 1rem)' }}
      >
        <Link
          to="/login"
          state={{ from: location.state?.from || undefined }}
          className="flex items-center justify-center w-10 h-10 bg-black/20 hover:bg-black/30 rounded-full backdrop-blur-sm transition-colors"
        >
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </Link>
      </div>

      <div
        className="lg:hidden absolute left-1/2 z-20 -translate-x-1/2 transform"
        style={{ top: 'calc(var(--app-safe-area-top) + 1.5rem)' }}
      >
        <Link to="/login" state={{ from: location.state?.from || undefined }}>
          <img src="/logo_white.webp" alt="OKIO" className="h-8 w-auto mx-auto" />
        </Link>
      </div>

      <div className="relative z-10 h-full flex flex-col lg:flex-row">
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/home/forrest2.jpeg')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-gray-900/20 to-black/30"></div>
          </div>
        </div>

        <div className="flex-1 lg:w-1/2 flex items-end lg:items-center justify-center lg:bg-white lg:dark:bg-gray-900 lg:py-12 px-0 lg:px-8">
          <div className="w-full lg:max-w-md bg-white dark:bg-gray-900 lg:bg-transparent rounded-t-3xl lg:rounded-none shadow-2xl lg:shadow-none px-6 pt-8 pb-5 lg:p-0 lg:pt-0 space-y-4 lg:space-y-8">
            <div className="text-center mb-6 lg:mb-6">
              <Link
                to="/login"
                state={{ from: location.state?.from || undefined }}
                className="hidden lg:block"
              >
                <img
                  src="/logo_black.webp"
                  alt="OKIO"
                  className="h-8 w-auto mx-auto mb-4 dark:hidden"
                />
                <img
                  src="/logo_white.webp"
                  alt="OKIO"
                  className="h-8 w-auto mx-auto mb-4 hidden dark:block"
                />
              </Link>
              <h2 className="mt-0 lg:mt-4 text-xl font-extrabold text-gray-900 dark:text-white">
                Create an account with email
              </h2>
            </div>

            <form className="space-y-4 lg:space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-3 lg:space-y-4">
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Username
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      value={formData.username}
                      onChange={handleChange}
                      className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                        errors.username
                          ? 'border-red-400'
                          : usernameStatus === 'available'
                            ? 'border-green-400'
                            : usernameStatus === 'taken' ||
                                usernameStatus === 'invalid'
                              ? 'border-red-400'
                              : 'border-gray-300 dark:border-gray-600'
                      } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                      placeholder="Choose a username"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {usernameStatus === 'checking' &&
                        formData.username.length >= 3 && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-300"></div>
                        )}
                      {usernameStatus === 'available' && (
                        <CheckIcon className="h-5 w-5 text-green-400" />
                      )}
                      {(usernameStatus === 'taken' ||
                        usernameStatus === 'invalid') && (
                        <XMarkIcon className="h-5 w-5 text-red-400" />
                      )}
                    </div>
                  </div>

                  {errors.username && (
                    <p className="mt-1 text-sm text-red-400">
                      {errors.username}
                    </p>
                  )}
                  {!errors.username && usernameStatus === 'available' && (
                    <p className="mt-1 text-sm text-green-400">
                      Username is available
                    </p>
                  )}
                  {!errors.username && usernameStatus === 'taken' && (
                    <p className="mt-1 text-sm text-red-400">
                      Username is already taken
                    </p>
                  )}
                  {!errors.username && usernameStatus === 'invalid' && (
                    <p className="mt-1 text-sm text-red-400">
                      Username must start with a letter or number and can only
                      contain letters, numbers, and underscores
                    </p>
                  )}
                  {!errors.username &&
                    formData.username &&
                    formData.username.length >= 3 &&
                    usernameStatus === 'checking' && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Checking availability...
                      </p>
                    )}
                  {!formData.username && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      3-30 characters, start with a letter or number, and use
                      only letters, numbers, and underscores
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                      errors.email
                        ? 'border-red-400'
                        : 'border-gray-300 dark:border-gray-600'
                    } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                        errors.password
                          ? 'border-red-400'
                          : 'border-gray-300 dark:border-gray-600'
                      } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-400">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Confirm password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                        errors.confirmPassword
                          ? 'border-red-400'
                          : 'border-gray-300 dark:border-gray-600'
                      } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-400">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 dark:focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create account'
                )}
              </button>

              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Already have an email account?
                </p>
                <Link
                  to="/login"
                  state={{ from: location.state?.from || undefined }}
                  className="block w-full py-3 px-6 text-center text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                >
                  Sign in here
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;